import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';

/**
 * Hook personalizado para gerenciar API com retry automático de token
 * Especialmente útil quando o tenant muda e o token pode estar expirado
 */
export const useApiWithRetry = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  /**
   * Obtém o token com retry automático
   */
  const getTokenWithRetry = useCallback(async (): Promise<string | null> => {
    try {
      // Primeira tentativa - usar cache
      return await getAccessTokenSilently();
    } catch (error) {
      console.warn('Erro ao obter token (primeira tentativa):', error);

      try {
        return await getAccessTokenSilently({
          cacheMode: 'off',
          timeoutInSeconds: 10
        });
      } catch (retryError) {
        console.error('Erro ao renovar token:', retryError);
        
        // Se o erro é relacionado a login, redirecionar para login
        const errorMessage = String(retryError);
        if (errorMessage.includes('Login required') || errorMessage.includes('login_required')) {
          console.log('Token inválido ou expirado. Redirecionando para login...');
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname }
          });
        }
        
        return null;
      }
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  /**
   * Faz uma requisição HTTP com retry automático
   */
  const apiRequest = useCallback(async <T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> => {
    const API_BASE_URL = import.meta.env.VITE_ORCHESTRATOR_CONSOLE_API_URL || 'http://localhost:8000';
    // Normalizar a URL base para garantir que termine com /
    const normalizedBaseURL = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`;
    // Normalizar o endpoint para evitar barras duplas
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = `${normalizedBaseURL}${normalizedEndpoint}`;
  
    // Obter token com retry
    const token = await getTokenWithRetry();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      // Se a resposta é 401 Unauthorized, redirecionar para login
      if (response.status === 401) {
        console.log('Resposta 401: Não autorizado. Redirecionando para login...');
        await loginWithRedirect({
          appState: { returnTo: window.location.pathname }
        });
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.error || errorData.message || `HTTP ${response.status}`
        );
      }

      // Se a resposta for vazia, retornar null
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null as T;
      }

      return await response.json();
    } catch (error) {
      console.error('Erro na requisição API:', error);
      throw error;
    }
  }, [getTokenWithRetry, loginWithRedirect]);

  /**
   * Métodos HTTP
   */
  const get = useCallback(<T>(endpoint: string): Promise<T> => {
    return apiRequest<T>(endpoint, { method: 'GET' });
  }, [apiRequest]);

  const post = useCallback(<T, D = Record<string, unknown>>(endpoint: string, data?: D): Promise<T> => {
    return apiRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }, [apiRequest]);

  const put = useCallback(<T, D = Record<string, unknown>>(endpoint: string, data?: D): Promise<T> => {
    return apiRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }, [apiRequest]);

  const del = useCallback(<T>(endpoint: string): Promise<T> => {
    return apiRequest<T>(endpoint, { method: 'DELETE' });
  }, [apiRequest]);

  return {
    get,
    post,
    put,
    delete: del,
    getTokenWithRetry,
  };
};
